import { Link } from "react-router-dom";
import { Train, Car, Plane, MapPin, Calendar, Building2, Star } from "lucide-react";
import { StayAreaCard } from "@/components/Cards";
import { CTAButton } from "@/components/CTAButton";
import { AdBlock } from "@/components/AdBlock";
import { onImgError } from "@/lib/images";

const PRODUCTS = [
  { type: "hotels", label: "Hotels" },
  { type: "flights", label: "Search Flights" },
];

// Shared body for venue-guide and event-guide (ctx flows subid tracking context).
export function VenueGuideBody({ bundle, ctx, pageName }) {
  const { venue, city, teams, events, stay_areas = [], hotels = [], guides = [] } = bundle;
  if (!venue) return null;
  const best = stay_areas[0];

  return (
    <>
      {/* Practical logistics */}
      <div className="grid gap-5 md:grid-cols-3 mb-14">
        <LogisticCard icon={Train} title="Transit" body={venue.transit_overview} />
        <LogisticCard icon={Plane} title="Airports" body={venue.airport_notes} />
        <LogisticCard icon={MapPin} title="Address" body={venue.address} extra={venue.capacity ? `Capacity ${venue.capacity.toLocaleString()} • Opened ${venue.opened}` : null} />
      </div>

      {/* Commerce quick bar */}
      <div className="rounded-2xl border border-black/5 bg-card card-shadow p-5 mb-14">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="overline text-primary mb-1">Book your trip</div>
            <p className="text-sm text-muted-foreground">Hotelstrips helps you decide first — then jump to the right provider.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {PRODUCTS.map((p) => (
              <CTAButton key={p.type} productType={p.type} label={p.label} size="sm" variant="ghost"
                testId={`venue-cta-${p.type}`}
                ctx={{ ...ctx, page: pageName, venue: venue.slug, city: city?.slug, position: "logistics_bar" }} />
            ))}
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Some links are affiliate links — Hotelstrips may earn a commission when you book.{" "}
          <Link to="/affiliate-disclosure" className="underline hover:text-primary">Disclosure</Link>.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Flights open Hotelstrips-branded flight booking powered by Nuitée in a new tab — review the total, fees &amp; fare conditions before payment.
        </p>
      </div>

      {/* Stay areas — the core recommendation layer */}
      <div className="mb-14">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="overline text-primary mb-2">Stay area intelligence</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-ink">Best areas to stay near {venue.name}</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">Ranked by Hotelstrips — not a booking provider. Each area is scored on access, transit, nightlife, family fit and price.</p>
          </div>
        </div>

        {best && (
          <div className="mb-6 rounded-2xl bg-ink text-white overflow-hidden grid md:grid-cols-[1.2fr_1fr]">
            <div className="p-8">
              <span className="inline-block rounded-full bg-primary px-3 py-1 text-xs font-heading font-bold text-primary-foreground mb-3">{best._badge || best.best_for}</span>
              <h3 className="font-heading text-3xl font-black tracking-tighter">{best.name}</h3>
              <p className="text-white/70 mt-3 leading-relaxed">{best.editorial_recommendation || best.summary}</p>
              <p className="text-sm text-white/60 mt-3">{best._access}</p>
              <div className="mt-5">
                {best._hotel_link_scope === "none" ? (
                  <span className="inline-flex items-center rounded-full bg-white/10 px-5 py-2.5 text-sm font-semibold text-white/50 cursor-not-allowed">Hotel search unavailable</span>
                ) : (
                  <CTAButton productType="hotels" label={best._hotel_link_scope === "neighborhood" ? `Find hotels in ${best.name}` : "Search hotels · city-wide"} size="md"
                    ctx={{ ...ctx, page: pageName, venue: venue.slug, stayArea: best.slug, city: best.city_slug, position: "best_overall" }} />
                )}
              </div>
            </div>
            <div className="relative min-h-[220px]">
              <img src={best.image_url} alt={best.image_alt || best.name} onError={onImgError} className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
        )}

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {stay_areas.map((a, i) => (
            <StayAreaCard key={a.slug} area={a} index={i} ctx={{ ...ctx, page: pageName, venue: venue.slug }} />
          ))}
        </div>
      </div>

      {/* Sponsor slot (disabled by default) */}
      <div className="mb-14">
        <AdBlock placement="venue_guide" enabled={false} />
      </div>

      {/* Guides */}
      {guides.length > 0 && (
        <div className="mb-14">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink mb-5">Guides for this trip</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {guides.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} className="group rounded-xl overflow-hidden bg-card border border-black/5 card-shadow transition-transform duration-300 hover:-translate-y-1">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={g.hero_image} alt={g.title} loading="lazy" onError={onImgError} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="overline text-primary mb-2">{g.category}</div>
                  <h3 className="font-heading text-lg font-bold text-ink leading-tight">{g.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function LogisticCard({ icon: Icon, title, body, extra }) {
  return (
    <div className="rounded-xl bg-card border border-black/5 card-shadow p-6">
      <span className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
      <div className="overline text-muted-foreground mt-4">{title}</div>
      <p className="text-sm text-foreground/80 mt-1.5 leading-relaxed">{body}</p>
      {extra && <p className="text-xs text-muted-foreground mt-2">{extra}</p>}
    </div>
  );
}
